import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import auth from '@udacity/ureact-hoth';
import { useHistory } from 'react-router-dom';
import { Select, SingleValue } from 'chakra-react-select';
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { InfoIcon } from '@udacity/chakra-uds-icons';
import { getChakraReactSelectStyles } from '@udacity/chakra-uds-theme';
import Analytics from '@udacity/ureact-analytics';

import { useMarkUngradeableMutation, useUnassignSubmissionMutation, useUploadFileMutation } from 'app/apis/reviews';
import { Modal } from 'app/common/components/modal';

import { FeedbackInput } from './feedback-input';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { useIsCrossLangReview } from 'app/project-reviews/hooks/use-is-cross-lang-review';
import { DEFAULT_LANG_CLIENT } from 'app/i18n';

type Option = {
  value: string;
  label: string;
  helperText: string;
  subtitle?: string;
  tooltip?: string;
};

const ZIPPED_FILE_TYPES = ['application/zip', 'application/x-zip', 'application/x-zip-compressed'];

type UnableToReviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  submissionId: number;
  rubricLanguage: string;
};

export const UnableToReviewModal: React.FC<UnableToReviewModalProps> = ({
  isOpen,
  onClose,
  submissionId,
  rubricLanguage,
}) => {
  const [standardReason, setStandardReason] = useState<SingleValue<Option>>(null);
  const [escalationReason, setEscalationReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [plagiarismSourceUrl, setPlagiarismSourceUrl] = useState('');
  const [plagiarismAttachmentUrl, setPlagiarismAttachmentUrl] = useState('');
  const [hasError, setHasError] = useState(false);
  const currentUserUid = auth.getCurrentUserId();
  const isCrossLangReview = useIsCrossLangReview(submissionId);
  const { t } = useTranslation();
  const history = useHistory();
  const toast = useAppToast();

  const [unassignMyself, { isLoading: isUnassigning }] = useUnassignSubmissionMutation();
  const [markUngradeable, { isLoading: isMarkingUngradeable }] = useMarkUngradeableMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const standardReasonOptions = useMemo<Option[]>(
    () => [
      {
        value: 'missing_requirements',
        label: t('projectReviews.unableToReview.missingRequirements'),
        helperText: t('projectReviews.unableToReview.missingRequirementsHint'),
      },
      {
        value: 'plagiarism',
        label: t('projectReviews.plagiarism.plagiarism'),
        helperText: t('projectReviews.unableToReview.plagiarismHint'),
      },
      {
        value: 'unassign',
        label: t('projectReviews.unableToReview.unassigningMyself'),
        helperText: t('projectReviews.unableToReview.unassigningMyselfHint'),
      },
      {
        value: 'language',
        label: t('projectReviews.unableToReview.wrongLanguage'),
        helperText: t('projectReviews.unableToReview.wrongLanguageHint'),
      },
    ],
    [t]
  );

  const escalationReasonOptions = useMemo<Option[]>(
    () => [
      {
        value: 'repeat_submission',
        label: t('projectReviews.unableToReview.repeatSubmission'),
        subtitle: t('projectReviews.unableToReview.repeatSubmissionSubtitle'),
        helperText: t('projectReviews.unableToReview.repeatSubmissionHint'),
        tooltip: t('projectReviews.unableToReview.repeatSubmissionTooltip'),
      },
      {
        value: 'abuse',
        label: t('projectReviews.unableToReview.abusiveConduct'),
        subtitle: t('projectReviews.unableToReview.abusiveConductSubtitle'),
        helperText: t('projectReviews.unableToReview.abusiveConductHint'),
        tooltip: t('projectReviews.unableToReview.abusiveConductTooltip'),
      },
    ],
    [t]
  );

  const reason = useMemo(() => {
    if (escalationReason) {
      return escalationReasonOptions.find((option) => option.value === escalationReason) ?? null;
    }

    return standardReason;
  }, [escalationReason, escalationReasonOptions, standardReason]);

  const shownToStudent = ['missing_requirements', 'plagiarism', 'language', 'repeat_submission'].includes(
    reason?.value ?? ''
  );
  const helperTextShownToStudent = ['missing_requirements', 'plagiarism', 'repeat_submission'].includes(
    reason?.value ?? ''
  );
  const shownToStaff = ['abuse', 'unassign'].includes(reason?.value ?? '');
  const shouldShowCrossLangFeedbackHelperText = isCrossLangReview && helperTextShownToStudent;

  const isInvalid = useMemo(() => {
    return !reason || !reasonDetails || (reason.value === 'plagiarism' && !plagiarismSourceUrl);
  }, [reason, reasonDetails, plagiarismSourceUrl]);

  const prevReasonValueRef = useRef(reason?.value);

  useEffect(() => {
    const currentReason = reason?.value;

    if (currentReason === 'language') {
      const response = isCrossLangReview
        ? t('projectReviews.unableToReview.wrongLanguageResponse', {
            lng: DEFAULT_LANG_CLIENT,
          })
        : t('projectReviews.unableToReview.wrongLanguageResponse', {
            lng: rubricLanguage.toLowerCase(),
          });
      setReasonDetails(response);
    } else if (prevReasonValueRef.current !== currentReason) {
      setReasonDetails('');
    }

    prevReasonValueRef.current = currentReason;
  }, [reason?.value, isCrossLangReview, rubricLanguage, t]);

  const handleStandardReasonChange = (newValue: SingleValue<Option>) => {
    setStandardReason(newValue);
    setEscalationReason('');
  };

  const handleEscalationReasonChange = (value: string) => {
    setEscalationReason(value);
    setStandardReason(null);
  };

  const resetForm = () => {
    setStandardReason(null);
    setEscalationReason('');
    setReasonDetails('');
    setPlagiarismSourceUrl('');
    setPlagiarismAttachmentUrl('');
    setHasError(false);
    prevReasonValueRef.current = undefined;
  };

  useEffect(() => {
    resetForm();
  }, [isOpen]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setHasError(false);
    if (!reason?.value) return;

    if (reason.value === 'unassign') {
      Analytics.track('Project Review Unassigned', {
        timestamp: new Date().toISOString(),
        submissionId,
        reviewerId: currentUserUid,
      });
      await unassignMyself({ submissionId, reason: reasonDetails })
        .unwrap()
        .then(() => {
          resetForm();
          onClose();
          history.push('/queue/overview');
        })
        .catch((e) => {
          console.error(e);
          setHasError(true);
        });

      return;
    }

    Analytics.track('Project Review Marked Ungradeable', {
      timestamp: new Date().toISOString(),
      submissionId,
      reviewerId: currentUserUid,
    });

    await markUngradeable({
      submissionId,
      notes: reasonDetails,
      tag: reason.value,
      plagiarismSourceUrl: plagiarismSourceUrl ?? null,
      plagiarismAttachmentUrl: plagiarismAttachmentUrl ?? null,
    })
      .then(() => {
        resetForm();
        onClose();
        history.push('/queue/overview');
      })
      .catch((e) => {
        console.error(e);
        setHasError(true);
      });
  };

  const handlePlagiarismFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const isZip = ZIPPED_FILE_TYPES.includes(file.type);
      if (!isZip) throw new Error('Invalid file type expected zip file');

      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadFile(formData).unwrap();

      if (response.url) {
        setPlagiarismAttachmentUrl(response.url);
      }
    } catch (e) {
      console.error(e);

      toast({
        variant: 'error',
        description: t('projectReviews.uploadZipError', { message: (e as Error).message }),
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('projectReviews.unableToReview.whyCantBeReviewed')}
      footer={
        <>
          {hasError && (
            <Text color="feedback.negative" size="label">
              {t('error.anErrorOccurred')}
            </Text>
          )}

          <Flex gap={6} w="full">
            <Button onClick={handleSubmit} isDisabled={isInvalid} isLoading={isUnassigning || isMarkingUngradeable}>
              {t('common.submit')}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </Flex>
        </>
      }
      footerStyles={{ gap: 6, flexDir: 'column' }}
    >
      <>
        <Text mb={6}>{t('projectReviews.unableToReview.afterSubmittingAReason')}</Text>

        <FormControl mb={8} maxW="25rem">
          <FormLabel mb={2}>{t('projectReviews.unableToReview.selectReason')}</FormLabel>
          <Select<Option>
            chakraStyles={getChakraReactSelectStyles()}
            value={standardReason}
            options={standardReasonOptions}
            onChange={handleStandardReasonChange}
            menuPosition="fixed"
          />
        </FormControl>

        <FormControl mb={8}>
          <FormLabel mb={2}>{t('projectReviews.unableToReview.escalationPaths')}</FormLabel>
          <RadioGroup value={escalationReason} onChange={handleEscalationReasonChange}>
            <Stack spacing={4}>
              {escalationReasonOptions.map((option) => (
                <Tooltip key={option.value} label={option.tooltip} placement="top-start" hasArrow>
                  <Box borderWidth="1px" borderRadius="md" p={4}>
                    <Radio value={option.value}>
                      <Text fontWeight="semibold">{option.label}</Text>
                    </Radio>
                    {option.subtitle && (
                      <Text mt={1} ms={6} size="label" color="gray.600">
                        {option.subtitle}
                      </Text>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {reason?.value === 'plagiarism' && (
          <FormControl mb={4}>
            <FormLabel mb={2}>{t('projectReviews.plagiarism.pleaseEnterUrl')}</FormLabel>
            <Input value={plagiarismSourceUrl} onChange={(e) => setPlagiarismSourceUrl(e.target.value)} />
          </FormControl>
        )}

        {reason?.value === 'plagiarism' && (
          <FormControl mb={4}>
            <FormLabel mb={2}>{t('projectReviews.pleaseAttachFile')}</FormLabel>
            {isUploading ? (
              <Flex alignItems="center" gap={2}>
                <Spinner size="sm" />
                <FormHelperText mb={2}>{t('projectReviews.uploading')}</FormHelperText>
              </Flex>
            ) : (
              <FormHelperText mb={2}>{t('projectReviews.uploadZipFileOnly')}</FormHelperText>
            )}
            <Input
              type="file"
              accept={ZIPPED_FILE_TYPES.join(',')}
              multiple={false}
              onChange={handlePlagiarismFileChange}
              height="auto"
            />
          </FormControl>
        )}

        {reason && (
          <FormControl>
            <FormLabel mb={2}>{t('projectReviews.unableToReview.pleaseExplain')}</FormLabel>
            <FormHelperText mb={2}>{reason.helperText}</FormHelperText>
            {shouldShowCrossLangFeedbackHelperText && (
              <FormHelperText mb={2} fontWeight="bold" fontSize="sm">
                {t('projectReviews.projectReview.crossLangFeedbackHelper')}
              </FormHelperText>
            )}

            {(shownToStudent || shownToStaff) && (
              <Alert
                status="info"
                mb={4}
                borderStart="8px solid"
                borderStartColor="blue.500"
                sx={{
                  '.chakra-alert__desc': { padding: '1rem 1rem 1rem 0.5rem' },
                }}
              >
                <InfoIcon w={8} h={8} color="blue.500" ms={2} />
                <AlertDescription>
                  {shownToStudent
                    ? t('projectReviews.unableToReview.shownToStudent')
                    : t('projectReviews.unableToReview.shownToStaff')}
                </AlertDescription>
              </Alert>
            )}

            <FeedbackInput
              id="ungradeable-reason"
              feedback={reasonDetails}
              setFeedback={setReasonDetails}
              isAssigned
              isDisabled={reason?.value === 'language'}
              isCrossLangReview={isCrossLangReview}
              isImageHelperTextDisplayed={shownToStudent}
            />
          </FormControl>
        )}
      </>
    </Modal>
  );
};
